import { type QueryClient, queryOptions } from "@tanstack/react-query";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import { getRequest, getRequestHeaders } from "@tanstack/react-start/server";
import {
    type AuthState,
    authStateSchema,
} from "@wags/viewer/identity/viewer-identity.contract.ts";
import type { ViewerRef } from "@wags/viewer/index.ts";
import { getAuth } from "./auth-protocol.server.ts";
import type { AuthIdentitySession } from "./auth-identity-dependencies.interface.ts";
import type IAuthIdentityDependencies from "./auth-identity-dependencies.interface.ts";
import { isKnownCrawler } from "./is-known-crawler.ts";

export const AUTH_QUERY_KEY = ["auth-state"] as const;
export type { AuthState };

export function seedAuthState(
    queryClient: QueryClient,
    authState: AuthState | null,
) {
    if (queryClient.getQueryData(AUTH_QUERY_KEY) !== undefined) return;
    queryClient.setQueryData(AUTH_QUERY_KEY, authState);
}

export function requireViewer<T>(viewer: T | null): T {
    if (!viewer) {
        throw new Error("Viewer identity is required for this mutation.");
    }
    return viewer;
}

export async function resolveViewerProfileIdentityWithDependencies(
    dependencies: IAuthIdentityDependencies,
    headers: Headers,
): Promise<(AuthState & { authUserId: string }) | null> {
    const session = await dependencies.getSession();
    if (!session && isKnownCrawler(headers)) return null;
    const resolvedSession = session ?? (await dependencies.signInAnonymous());
    const user = resolvedSession.user;
    return {
        appUserId: user.profileId,
        authUserId: user.id,
        isAuthenticated: !user.isAnonymous,
    };
}

function identitySessionFromAuthUser(user: {
    id: string;
    isAnonymous: boolean;
    profileId: string;
}): AuthIdentitySession {
    return {
        user: {
            id: user.id,
            isAnonymous: user.isAnonymous,
            profileId: user.profileId,
        },
    };
}

const viewerProfileIdentityByRequest = new WeakMap<
    Request,
    ReturnType<typeof lookupViewerProfileIdentity>
>();

export const resolveViewerProfileIdentity = createServerOnlyFn(async () => {
    const request = getRequest();
    let pending = viewerProfileIdentityByRequest.get(request);
    if (!pending) {
        pending = lookupViewerProfileIdentity().catch((error) => {
            viewerProfileIdentityByRequest.delete(request);
            throw error;
        });
        viewerProfileIdentityByRequest.set(request, pending);
    }
    return await pending;
});

async function lookupViewerProfileIdentity() {
    const auth = await getAuth();
    const headers = getRequestHeaders();
    return await resolveViewerProfileIdentityWithDependencies(
        {
            getSession: async () => {
                const session = await auth.api.getSession({ headers });
                if (!session) return null;
                const user = session.user as typeof session.user & {
                    isAnonymous: boolean;
                    profileId: string;
                };
                return identitySessionFromAuthUser(user);
            },
            signInAnonymous: async () => {
                const session = await auth.api.signInAnonymous({ headers });
                const user = session.user as typeof session.user & {
                    isAnonymous: boolean;
                    profileId: string;
                };
                return identitySessionFromAuthUser(user);
            },
        },
        headers,
    );
}

export const resolveViewerRef = createServerOnlyFn(
    async (): Promise<ViewerRef | null> => {
        const viewer = await resolveViewerProfileIdentity();
        if (!viewer) return null;
        return {
            viewerId: viewer.appUserId,
            isAuthenticated: viewer.isAuthenticated,
        };
    },
);

type SessionUser = {
    id: string;
    isAnonymous: boolean;
    profileId: string;
};

function sessionUserFrom(user: object): SessionUser {
    const id = Reflect.get(user, "id");
    const profileId = Reflect.get(user, "profileId");
    const isAnonymous = Reflect.get(user, "isAnonymous");
    if (
        typeof id !== "string" ||
        typeof profileId !== "string" ||
        typeof isAnonymous !== "boolean"
    ) {
        throw new Error("Session user is missing profile fields.");
    }
    return { id, profileId, isAnonymous };
}

function authStateFromSessionUser(user: SessionUser): AuthState {
    return authStateSchema.parse({
        appUserId: user.profileId,
        authUserId: user.id,
        isAuthenticated: !user.isAnonymous,
    });
}

export const resolveExistingViewerRef = createServerOnlyFn(
    async (): Promise<ViewerRef | null> => {
        const session = await (
            await getAuth()
        ).api.getSession({
            headers: getRequestHeaders(),
        });
        if (!session) return null;
        const user = session.user as typeof session.user & {
            isAnonymous: boolean;
            profileId: string;
        };
        return {
            viewerId: user.profileId,
            isAuthenticated: !user.isAnonymous,
        };
    },
);

export const readRequestAuthState = createServerFn({ method: "GET" }).handler(
    async (): Promise<AuthState | null> => {
        const session = await (
            await getAuth()
        ).api.getSession({
            headers: getRequestHeaders(),
        });
        if (!session) return null;
        return authStateFromSessionUser(sessionUserFrom(session.user));
    },
);

const getAuthState = createServerFn({ method: "GET" }).handler(async () => {
    const viewer = await resolveViewerProfileIdentity();
    return viewer ? authStateSchema.parse(viewer) : null;
});

export const authQueryOptions = () =>
    queryOptions({
        queryKey: AUTH_QUERY_KEY,
        queryFn: () => getAuthState(),
        staleTime: 60_000,
    });
